import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { Save, ImagePlus, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminConfig() {
  const { fetchConfig } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    api.get("/admin/config").then((r) => setForm(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { setError("Logo maksimal 1.5MB"); return; }
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => set("logo", ev.target.result);
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        logo: form.logo,
        address: form.address,
        contact_phone: form.contact_phone,
        contact_email: form.contact_email,
        simpanan_pokok_default: parseFloat(form.simpanan_pokok_default) || 0,
        simpanan_wajib_default: parseFloat(form.simpanan_wajib_default) || 0,
      };
      const { data } = await api.put("/admin/config", payload);
      setForm(data);
      setSuccess("Konfigurasi berhasil disimpan");
      toast.success("Konfigurasi berhasil disimpan");
      fetchConfig();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <AdminLayout><div className="text-brand-muted">Memuat konfigurasi…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Konfigurasi Koperasi</h1>
          <p className="text-brand-muted text-sm mt-1">Atur nama, logo, dan kontak koperasi yang tampil di seluruh aplikasi.</p>
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3">{error}</div>}
        {success && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3">{success}</div>}

        <form onSubmit={save} className="bg-white rounded-3xl border border-brand/10 shadow-sm p-5 sm:p-7 space-y-5" data-testid="config-form">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-brand-soft flex items-center justify-center overflow-hidden border border-brand/15">
              {form.logo ? <img src={form.logo} alt="logo" className="w-full h-full object-cover" /> : <Building2 className="text-brand" />}
            </div>
            <label className="rounded-full border border-brand/20 px-4 py-2 text-sm flex items-center gap-2 cursor-pointer hover:bg-brand-soft" data-testid="config-upload-logo">
              <ImagePlus size={14} /> Unggah Logo
              <input type="file" accept="image/*" onChange={onLogo} className="hidden" />
            </label>
            {form.logo && <button type="button" onClick={() => set("logo", null)} className="text-sm text-amber-700 hover:underline">Hapus</button>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nama Koperasi"><input className="inp" required value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="config-name-input" /></Field>
            <Field label="Tagline"><input className="inp" value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="config-tagline-input" /></Field>
            <Field label="Email Kontak"><input className="inp" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Telepon Kontak"><input className="inp" value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="Default Simpanan Pokok (Rp)"><input className="inp" type="number" min="0" value={form.simpanan_pokok_default} onChange={(e) => set("simpanan_pokok_default", e.target.value)} data-testid="config-pokok-input" /></Field>
            <Field label="Default Simpanan Wajib (Rp)"><input className="inp" type="number" min="0" value={form.simpanan_wajib_default} onChange={(e) => set("simpanan_wajib_default", e.target.value)} data-testid="config-wajib-input" /></Field>
          </div>
          <Field label="Alamat"><textarea className="inp h-24" value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>

          <div className="flex justify-end">
            <LoadingButton type="submit" loading={saving} loadingText="Menyimpan…" data-testid="config-save-btn" className="rounded-full bg-brand text-white px-6 py-2.5 text-sm hover:bg-brand-hover">
              <Save size={14} /> Simpan Konfigurasi
            </LoadingButton>
          </div>
          <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
        </form>
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.16em]">{label}</div>
      {children}
    </label>
  );
}
